import { supabase } from '../lib/supabase';
import { emailNotificationService } from './emailNotificationService';

export const payslipService = {
  // ============================================================================
  // PAYSLIP GENERATION FROM APPROVED PAYROLL RUNS
  // ============================================================================

  async generatePayslipsFromPayrollRun(payrollRunId, generatedBy) {
    try {
      // 1. Get payroll run details
      const { data: payrollRun, error: runError } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.eq('id', payrollRunId)
        ?.eq('payroll_status', 'processed')
        ?.single();

      if (runError) throw runError;
      if (!payrollRun) throw new Error('Payroll run not found or not processed');

      // 2. Get payroll details for all employees in this run
      const { data: payrollDetails, error: detailsError } = await supabase
        ?.from('payroll_details')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email
          )
        `)
        ?.eq('payroll_run_id', payrollRunId);

      if (detailsError) throw detailsError;
      if (!payrollDetails || payrollDetails?.length === 0) {
        throw new Error('No payroll details found for this run');
      }

      // 3. Generate payslip number (format: PS-YYYYMMDD-XXX)
      const datePrefix = new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '');
      
      // 4. Create payslips for each employee
      const payslipsToInsert = payrollDetails?.map((detail, index) => {
        const payslipNumber = `PS-${datePrefix}-${String(index + 1)?.padStart(3, '0')}`;
        
        return {
          payslip_number: payslipNumber,
          payroll_run_id: payrollRunId,
          employee_id: detail?.employee_id,
          payroll_detail_id: detail?.id,
          pay_period_start: payrollRun?.pay_period_start,
          pay_period_end: payrollRun?.pay_period_end,
          payment_date: payrollRun?.payment_date,
          payslip_status: 'generated',
          generated_at: new Date()?.toISOString()
        };
      });

      // 5. Insert payslips
      const { data: insertedPayslips, error: insertError } = await supabase
        ?.from('payslips')
        ?.insert(payslipsToInsert)
        ?.select();

      if (insertError) throw insertError;

      // 6. Send email notifications to employees
      for (const payslip of insertedPayslips || []) {
        const payrollDetail = payrollDetails?.find(d => d?.id === payslip?.payroll_detail_id);
        if (payrollDetail?.employees?.email) {
          try {
            await emailNotificationService?.sendPayslipNotification(
              payrollDetail?.employees?.email,
              {
                id: payslip?.id,
                payslipNumber: payslip?.payslip_number,
                employeeId: payslip?.employee_id,
                employeeName: `${payrollDetail?.employees?.first_name || ''} ${payrollDetail?.employees?.last_name || ''}`?.trim(),
                payPeriodStart: new Date(payslip?.pay_period_start)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'short', year: 'numeric' }),
                payPeriodEnd: new Date(payslip?.pay_period_end)?.toLocaleDateString('en-PG', { day: '2-digit', month: 'short', year: 'numeric' }),
                paymentDate: payslip?.payment_date,
                grossPay: payrollDetail?.gross_pay,
                totalDeductions: payrollDetail?.deductions,
                swtAmount: payrollDetail?.swt_amount,
                nasfundEmployee: payrollDetail?.employee_super,
                netPay: payrollDetail?.net_pay,
                paymentMethod: 'Bank Transfer'
              }
            );

            // Update payslip status to 'sent'
            await supabase
              ?.from('payslips')
              ?.update({ 
                payslip_status: 'sent',
                sent_at: new Date()?.toISOString()
              })
              ?.eq('id', payslip?.id);
          } catch (emailError) {
            console.error(`Failed to send payslip notification to ${payrollDetail?.employees?.email}:`, emailError);
            // Update payslip status to 'failed' with error message
            await supabase
              ?.from('payslips')
              ?.update({ 
                payslip_status: 'failed',
                error_message: emailError?.message || 'Failed to send email notification'
              })
              ?.eq('id', payslip?.id);
          }
        }
      }

      return {
        success: true,
        payslipsGenerated: insertedPayslips?.length || 0,
        payslips: insertedPayslips
      };
    } catch (error) {
      console.error('Error generating payslips:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET PAYSLIPS WITH DETAILED BREAKDOWN
  // ============================================================================

  async getPayslipsByPayrollRun(payrollRunId) {
    try {
      const { data, error } = await supabase
        ?.from('payslips')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email,
            phone
          ),
          payroll_details (
            base_pay,
            overtime_pay,
            allowances,
            deductions,
            gross_pay,
            employee_super,
            employer_super,
            swt_amount,
            net_pay,
            hours_worked,
            overtime_hours
          ),
          payroll_runs (
            pay_period_start,
            pay_period_end,
            payment_date,
            payroll_status
          )
        `)
        ?.eq('payroll_run_id', payrollRunId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        payslipNumber: row?.payslip_number,
        payslipStatus: row?.payslip_status,
        generatedAt: row?.generated_at,
        sentAt: row?.sent_at,
        employee: {
          id: row?.employee_id,
          employeeNumber: row?.employees?.employee_number,
          firstName: row?.employees?.first_name,
          lastName: row?.employees?.last_name,
          fullName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
          department: row?.employees?.department,
          position: row?.employees?.position,
          email: row?.employees?.email,
          phone: row?.employees?.phone
        },
        payPeriod: {
          start: row?.payroll_runs?.pay_period_start,
          end: row?.payroll_runs?.pay_period_end,
          paymentDate: row?.payroll_runs?.payment_date
        },
        earnings: {
          basePay: row?.payroll_details?.base_pay || 0,
          overtimePay: row?.payroll_details?.overtime_pay || 0,
          allowances: row?.payroll_details?.allowances || 0,
          grossPay: row?.payroll_details?.gross_pay || 0
        },
        deductions: {
          employeeSuper: row?.payroll_details?.employee_super || 0,
          employerSuper: row?.payroll_details?.employer_super || 0,
          swtAmount: row?.payroll_details?.swt_amount || 0,
          otherDeductions: row?.payroll_details?.deductions || 0,
          totalDeductions: (
            (row?.payroll_details?.employee_super || 0) +
            (row?.payroll_details?.swt_amount || 0) +
            (row?.payroll_details?.deductions || 0)
          )
        },
        netPay: row?.payroll_details?.net_pay || 0,
        hours: {
          regularHours: row?.payroll_details?.hours_worked || 0,
          overtimeHours: row?.payroll_details?.overtime_hours || 0,
          totalHours: (row?.payroll_details?.hours_worked || 0) + (row?.payroll_details?.overtime_hours || 0)
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching payslips:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET SINGLE PAYSLIP DETAILS
  // ============================================================================

  async getPayslipById(payslipId) {
    try {
      const { data, error } = await supabase
        ?.from('payslips')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email,
            phone,
            date_of_birth,
            hire_date
          ),
          payroll_details (
            base_pay,
            overtime_pay,
            allowances,
            deductions,
            gross_pay,
            employee_super,
            employer_super,
            swt_amount,
            net_pay,
            hours_worked,
            overtime_hours
          ),
          payroll_runs (
            pay_period_start,
            pay_period_end,
            payment_date,
            payroll_status,
            processed_by,
            processed_at
          )
        `)
        ?.eq('id', payslipId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        payslipNumber: data?.payslip_number,
        payslipStatus: data?.payslip_status,
        generatedAt: data?.generated_at,
        sentAt: data?.sent_at,
        employee: {
          id: data?.employee_id,
          employeeNumber: data?.employees?.employee_number,
          firstName: data?.employees?.first_name,
          lastName: data?.employees?.last_name,
          fullName: `${data?.employees?.first_name} ${data?.employees?.last_name}`,
          department: data?.employees?.department,
          position: data?.employees?.position,
          email: data?.employees?.email,
          phone: data?.employees?.phone,
          dateOfBirth: data?.employees?.date_of_birth,
          hireDate: data?.employees?.hire_date
        },
        payPeriod: {
          start: data?.payroll_runs?.pay_period_start,
          end: data?.payroll_runs?.pay_period_end,
          paymentDate: data?.payroll_runs?.payment_date
        },
        earnings: {
          basePay: data?.payroll_details?.base_pay || 0,
          overtimePay: data?.payroll_details?.overtime_pay || 0,
          allowances: data?.payroll_details?.allowances || 0,
          grossPay: data?.payroll_details?.gross_pay || 0
        },
        deductions: {
          employeeSuper: data?.payroll_details?.employee_super || 0,
          employerSuper: data?.payroll_details?.employer_super || 0,
          swtAmount: data?.payroll_details?.swt_amount || 0,
          otherDeductions: data?.payroll_details?.deductions || 0,
          totalDeductions: (
            (data?.payroll_details?.employee_super || 0) +
            (data?.payroll_details?.swt_amount || 0) +
            (data?.payroll_details?.deductions || 0)
          )
        },
        netPay: data?.payroll_details?.net_pay || 0,
        hours: {
          regularHours: data?.payroll_details?.hours_worked || 0,
          overtimeHours: data?.payroll_details?.overtime_hours || 0,
          totalHours: (data?.payroll_details?.hours_worked || 0) + (data?.payroll_details?.overtime_hours || 0)
        },
        processedBy: data?.payroll_runs?.processed_by,
        processedAt: data?.payroll_runs?.processed_at
      };
    } catch (error) {
      console.error('Error fetching payslip:', error);
      throw error;
    }
  },

  // ============================================================================
  // UPDATE PAYSLIP STATUS
  // ============================================================================

  async markPayslipAsSent(payslipId) {
    try {
      const { data, error } = await supabase
        ?.from('payslips')
        ?.update({
          payslip_status: 'sent',
          sent_at: new Date()?.toISOString()
        })
        ?.eq('id', payslipId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        payslipStatus: data?.payslip_status,
        sentAt: data?.sent_at
      };
    } catch (error) {
      console.error('Error marking payslip as sent:', error);
      throw error;
    }
  },

  async bulkMarkPayslipsAsSent(payslipIds) {
    try {
      const { data, error } = await supabase
        ?.from('payslips')
        ?.update({
          payslip_status: 'sent',
          sent_at: new Date()?.toISOString()
        })
        ?.in('id', payslipIds)
        ?.select();

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        payslipStatus: row?.payslip_status,
        sentAt: row?.sent_at
      })) || [];
    } catch (error) {
      console.error('Error bulk marking payslips as sent:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET ALL APPROVED PAYROLL RUNS FOR PAYSLIP GENERATION
  // ============================================================================

  async getApprovedPayrollRuns() {
    try {
      const { data, error } = await supabase
        ?.from('payroll_runs')
        ?.select(`
          *,
          payslips (id)
        `)
        ?.eq('payroll_status', 'processed')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        payPeriodStart: row?.pay_period_start,
        payPeriodEnd: row?.pay_period_end,
        paymentDate: row?.payment_date,
        payrollStatus: row?.payroll_status,
        totalGross: row?.total_gross,
        totalNet: row?.total_net,
        totalSwt: row?.total_swt,
        totalNasfund: row?.total_nasfund,
        processedBy: row?.processed_by,
        processedAt: row?.processed_at,
        payslipsGenerated: row?.payslips?.length || 0,
        hasPayslips: (row?.payslips?.length || 0) > 0
      })) || [];
    } catch (error) {
      console.error('Error fetching approved payroll runs:', error);
      throw error;
    }
  },

  // ============================================================================
  // PAYSLIP STATISTICS
  // ============================================================================

  async getPayslipStatistics(payrollRunId) {
    try {
      const { data, error } = await supabase
        ?.from('payslips')
        ?.select('payslip_status')
        ?.eq('payroll_run_id', payrollRunId);

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        generated: data?.filter(p => p?.payslip_status === 'generated')?.length || 0,
        sent: data?.filter(p => p?.payslip_status === 'sent')?.length || 0,
        failed: data?.filter(p => p?.payslip_status === 'failed')?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching payslip statistics:', error);
      throw error;
    }
  }
};

export default payslipService;